import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Offer_Engagement_Type } from '@occc/graphql';
import {
  useIsFuelRollbackVoucherStackingEnabled,
  useRollbackStackingConfig,
} from '@occc-app/Chain/hooks';

import { OfferType } from '@occc-app/offerValidationService/types';
import { ConfirmDialog, Typography } from '@occc/ui-design-system';
import { useAppLayoutContextConsumer } from '@occc-app/design-system';
import { OfferCreateConfigurePage } from '@occc-app/routes/Offer/OfferCreatePage/OfferCreateConfigurePage';
import {
  CONFIGURE_PAGE_STATE,
  ConfigureFormInitialValues,
  ConfigureFormValues,
} from '@occc-app/routes/AwardCatalog/types';
import { getAwardCreatePageHeaderLinks, routeCreator } from '../utils';
import {
  getConfigureFormOperations,
  getTransformOfferToInitialValues,
} from '../components/ConfigureForm/utils';
import { AwardConfigureForm } from '../components/ConfigureForm/AwardConfigureForm';
import { messages } from '../messages';

export const AwardCatalogCreatePageContainer: React.FC = () => {
  const navigate = useNavigate();

  const links = useMemo(() => getAwardCreatePageHeaderLinks(), []);
  useAppLayoutContextConsumer(links);

  const isFuelRollbackVoucherStackingEnabled = useIsFuelRollbackVoucherStackingEnabled();
  const rollbackStackingConfig = useRollbackStackingConfig();

  const [isConfirmDialogOpened, setIsConfirmDialogOpened] = useState(false);
  const confirmCanceling = useCallback(() => navigate(routeCreator.base()), [navigate]);
  const cancel = useCallback(
    (hasUnsavedChanges: boolean) => {
      if (hasUnsavedChanges) {
        setIsConfirmDialogOpened(true);
        return;
      }
      confirmCanceling();
    },
    [confirmCanceling],
  );

  return (
    <OfferCreateConfigurePage<ConfigureFormValues, ConfigureFormInitialValues>
      pageTitle='Program Management > Create'
      subtitle={messages.subtitle}
      offerType={OfferType.Award}
      engagementType={Offer_Engagement_Type.Vouchered}
      getConfigureOperations={getConfigureFormOperations}
      getInitialValues={getTransformOfferToInitialValues({
        isFuelRollbackVoucherStackingEnabled,
        rollbackStackingConfig,
      })}
      onOfferCreated={(offerId) =>
        navigate(routeCreator.update(offerId), {
          state: { creatingState: CONFIGURE_PAGE_STATE.TARGET },
        })
      }
      renderConfigureForm={({ onSubmit, initialValues, loading, executing }) => (
        <>
          <AwardConfigureForm
            onSubmit={onSubmit}
            onCancelClick={cancel}
            initialValues={initialValues}
            loading={loading || executing}
          />
          <ConfirmDialog
            dialogTitle={messages.cancelDialogHeader}
            open={isConfirmDialogOpened}
            declineButtonText='No'
            onClose={() => setIsConfirmDialogOpened(false)}
            confirmButtonText='Yes'
            confirmButtonProps={{
              onClick: confirmCanceling,
            }}
          >
            <Typography>{messages.cancelDialogDescription}</Typography>
          </ConfirmDialog>
        </>
      )}
    />
  );
};
